import type { EverflowSubParams } from "@portal/app/_common/lib/everflowParameters";

export const everflowDomain = process.env.EVERFLOW_TRACKING_DOMAIN || "";
export const EVERFLOW_OFFER_ID = 1;
export const EVERFLOW_LOADED_EVENT_NAME = "everflow:loaded";

/**
 * Cookie name used by the SDK and local redirect capture for our offer's click IDs.
 * Derived from `ef_tid_c_o_<offer_id>` where `c` = click, `o` = offer.
 *
 * The SDK also writes `ef_tid_c_a_<advertiser_id>` (click by advertiser),
 * `ef_tid_i_o_<offer_id>` (impression by offer), and a legacy `ef_tid_<offer_id>`,
 * but we only use the click-by-offer cookie because this app only calls
 * EverflowSDK.click(), never impression().
 */
export const EVERFLOW_CLICK_OFFER_COOKIE_NAME = `ef_tid_c_o_${EVERFLOW_OFFER_ID}`;

/**
 * ADV parameter keys supported by Everflow.
 * This is the single source of truth - add new ADV keys here and they will
 * automatically be included in types and URL generation.
 */
export const EVERFLOW_ADV_KEYS = [
  "adv1",
  "adv2",
  "adv3",
  "adv4",
  "adv5",
  "adv6",
  "adv7",
  "adv8",
] as const;
export type EverflowAdvKey = (typeof EVERFLOW_ADV_KEYS)[number];
export type EverflowAdvParams = Partial<Record<EverflowAdvKey, string>>;

/**
 * Map of numbered Everflow events. (Note: There is no "event_id" field for the
 * CREATE_ACCOUNT event, so it's not included here.)
 */
export const EVERFLOW_EVENT_NAME_TO_ID_MAP = {
  // Private affiliate events
  // INTERNAL: Create an account - no event ID required
  // INTERNAL: Favorite a program
  FAVORITE_PROGRAM: 3,
  // INTERNAL: Complete school interest form
  SUBMIT_LEAD_CLIENT: 4,
  // INTERNAL: Submit lead to school
  SUBMIT_LEAD: 5,
  // EXTERNAL: Submit lead to school (without revenue information)
  SUBMIT_LEAD_WITHOUT_REVENUE: 10,
} as const;

export type EverflowEventName = keyof typeof EVERFLOW_EVENT_NAME_TO_ID_MAP;
export type EverflowEventId = (typeof EVERFLOW_EVENT_NAME_TO_ID_MAP)[EverflowEventName];

type EverflowBaseEvent = {
  transactionId: string;
  timestamp: EpochTimeStamp;
} & EverflowAdvParams;

export type EverflowCreateAccountEvent = EverflowBaseEvent;

export type EverflowFavoriteProgramEvent = EverflowBaseEvent & {
  eventId: typeof EVERFLOW_EVENT_NAME_TO_ID_MAP.FAVORITE_PROGRAM;
};

export type EverflowSubmitLeadEvent = EverflowBaseEvent & {
  eventId: typeof EVERFLOW_EVENT_NAME_TO_ID_MAP.SUBMIT_LEAD;
  amount: number;
};

export type EverflowSubmitLeadExternalEvent = EverflowBaseEvent & {
  eventId: typeof EVERFLOW_EVENT_NAME_TO_ID_MAP.SUBMIT_LEAD_WITHOUT_REVENUE;
};

export type EverflowEvent =
  | EverflowCreateAccountEvent
  | EverflowFavoriteProgramEvent
  | EverflowSubmitLeadEvent
  | EverflowSubmitLeadExternalEvent;

// FETCH CLICK RESPONSE TYPES

export type EverflowClickByIdResponse = Required<EverflowSubParams> & {
  transaction_id: string;
  is_unique: number;
  unix_timestamp: number;
  tracking_url: string;
  source_id: string;
  payout_type: string;
  revenue_type: string;
  payout: number;
  revenue: number;
  referer: string;
  previous_network_offer_id: number;
  error_code: number;
  project_id: string;
  user_ip: string;
  error_message: string;
  url: string;
  is_view_through: boolean;
  is_async: boolean;
  server_side_url: string;
  server_side_output: string;
  custom_landing_page_id: number;
  is_test_mode: boolean;
  idfa: string;
  idfa_md5: string;
  idfa_sha1: string;
  google_ad_id: string;
  google_ad_id_md5: string;
  google_ad_id_sha1: string;
  android_id: string;
  android_id_md5: string;
  android_id_sha1: string;
  error_filter_id: string;
  has_conversion: boolean;
  is_pass_through: boolean;
  creative_id: number;
  relationship: ClickByIdRelationship;
  coupon_code: string;
  redirect_method: string;
  is_sdk_click: boolean;
  category_id: number;
  currency_id: string;
};

type ClickByIdRelationship = {
  offer: ClickByIdOffer;
  advertiser: ClickByIdAdvertiser;
  account_manager: ClickByIdAccountManager;
  affiliate: ClickByIdAffiliate;
  affiliate_manager: ClickByIdAffiliateManager;
  geolocation: ClickByIdGeolocation;
  device_information: ClickByIdDeviceInformation;
  http_user_agent: string;
  http_accept_language: string;
  query_parameters: ClickByIdQueryParameters;
  redirect_url: string;
  forensiq_score: string;
};

type ClickByIdOffer = {
  network_offer_id: number;
  network_id: number;
  network_advertiser_id: number;
  network_offer_group_id: number;
  name: string;
  offer_status: string;
  network_tracking_domain_id: number;
  visibility: string;
  currency_id: string;
};

type ClickByIdAdvertiser = {
  network_advertiser_id: number;
  network_id: number;
  name: string;
  account_status: string;
};

type ClickByIdAccountManager = {
  network_employee_id: number;
  network_id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  account_status: string;
};

type ClickByIdAffiliate = {
  network_affiliate_id: number;
  network_id: number;
  name: string;
  account_status: string;
  network_traffic_source_id: number;
};

type ClickByIdAffiliateManager = {
  network_employee_id: number;
  network_id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  account_status: string;
};

type ClickByIdGeolocation = {
  country_code: string;
  country_name: string;
  region_code: string;
  region_name: string;
  city_name: string;
  dma: number;
  dma_name: string;
  timezone: string;
  carrier_name: string;
  carrier_code: number;
  organization: string;
  isp_name: string;
  is_mobile: boolean;
  is_proxy: boolean;
  postal_code: string;
};

type ClickByIdDeviceInformation = {
  is_mobile: boolean;
  platform_name: string;
  os_version: string;
  brand: string;
  model: string;
  is_tablet: boolean;
  browser_name: string;
  browser_version: string;
  device_type: string;
  language: string;
  http_accept_language: string;
  is_robot: boolean;
  is_filter: boolean;
};

type ClickByIdQueryParameters = {
  fbclid?: string;
  __cc: string;
  __efckuq: string;
  __qp: string;
  __rf: string;
  _ef_transaction_id: string;
  affid: string;
  async: string;
  oid: string;
  uid: string;
  source_id: string;
  sub1: string;
  sub2: string;
  sub3: string;
  sub4: string;
  sub5: string;
};

// FETCH CONVERSION RESPONSE TYPES

type ConversionRelationship = {
  offer: ConversionOffer;
  advertiser: ConversionAdvertiser;
  account_manager: ConversionAccountManager;
  affiliate: ConversionAffiliate;
  affiliate_manager: ConversionAffiliateManager;
  offer_url: ConversionOfferUrl;
  query_parameters: ConversionQueryParameters;
  redirect_url: string;
  referer: string;
  snapshots: ConversionSnapshot[];
  attribution_method: string;
  usm_data: unknown;
};

type ConversionOffer = {
  network_offer_id: number;
  network_id: number;
  name: string;
  offer_status: string;
};

type ConversionAdvertiser = {
  network_advertiser_id: number;
  network_id: number;
  name: string;
  account_status: string;
};

type ConversionAccountManager = {
  network_employee_id: number;
  network_id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  account_status: string;
};

type ConversionAffiliate = {
  network_affiliate_id: number;
  network_id: number;
  name: string;
  account_status: string;
};

type ConversionAffiliateManager = {
  network_employee_id: number;
  network_id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  account_status: string;
};

type ConversionOfferUrl = {
  network_offer_url_id: number;
  network_id: number;
  network_offer_id: number;
  name: string;
};

type ConversionQueryParameters = {
  amount: string;
  event_id: string;
  nid: string;
  timestamp: string;
  transaction_id: string;
};

type ConversionSnapshot = {
  conversion_history_snapshot_id: number;
  network_id: number;
  conversion_id: string;
  status: string;
  error_code: number;
  payout: number;
  revenue: number;
  sale_amount: number;
  snapshot_type: string;
  initiator: string;
  network_employee: unknown;
  unix_timestamp: number;
};

export type EverflowConversionByIdResponse = {
  conversion_id: string;
  conversion_unix_timestamp: number;
  sub1: string;
  sub2: string;
  sub3: string;
  sub4: string;
  sub5: string;
  source_id: string;
  status: string;
  payout_type: string;
  revenue_type: string;
  payout: number;
  revenue: number;
  session_user_ip: string;
  conversion_user_ip: string;
  country: string;
  region: string;
  city: string;
  dma: number;
  carrier: string;
  platform: string;
  os_version: string;
  device_type: string;
  device_model: string;
  brand: string;
  browser: string;
  language: string;
  http_user_agent: string;
  adv1: string;
  adv2: string;
  adv3: string;
  adv4: string;
  adv5: string;
  is_event: boolean;
  event: string;
  notes: string;
  transaction_id: string;
  click_unix_timestamp: number;
  error_code: number;
  error_message: string;
  sale_amount: number;
  is_scrub: boolean;
  coupon_code: string;
  order_id: string;
  url: string;
  isp: string;
  referer: string;
  app_id: string;
  idfa: string;
  idfa_md5: string;
  idfa_sha1: string;
  google_ad_id: string;
  google_ad_id_md5: string;
  google_ad_id_sha1: string;
  android_id: string;
  android_id_md5: string;
  android_id_sha1: string;
  currency_id: string;
  email: string;
  is_view_through: boolean;
  previous_network_offer_id: number;
  relationship: ConversionRelationship;
  network_offer_payout_revenue_id: number;
};

// FETCH TRANSACTION RESPONSE TYPES

/** Narrow read-only reporting contract; raw contact/cookie fields are not persisted. */
export type EverflowAdv1Conversion = Pick<EverflowConversionByIdResponse,
  "adv1" | "transaction_id" | "conversion_unix_timestamp" | "click_unix_timestamp" |
  "status" | "error_code" | "is_view_through" | "is_scrub"> & {
  network_affiliate_id?: number;
  relationship?: { affiliate?: { network_affiliate_id?: number } };
};
export type EverflowAdv1Report = {
  conversions: EverflowAdv1Conversion[];
  paging: { page: number; page_size: number; total_count: number };
};

type TransactionClick = {
  transaction_id: string;
  is_unique: number;
  unix_timestamp: number;
  tracking_url: string;
  source_id: string;
  sub1: string;
  sub2: string;
  sub3: string;
  sub4: string;
  sub5: string;
  payout_type: string;
  revenue_type: string;
  payout: number;
  revenue: number;
  referer: string;
  previous_network_offer_id: number;
  error_code: number;
  project_id: string;
  user_ip: string;
  error_message: string;
  url: string;
  is_view_through: boolean;
  is_async: boolean;
  server_side_url: string;
  server_side_output: string;
  custom_landing_page_id: number;
  is_test_mode: boolean;
  idfa: string;
  idfa_md5: string;
  idfa_sha1: string;
  google_ad_id: string;
  google_ad_id_md5: string;
  google_ad_id_sha1: string;
  android_id: string;
  android_id_md5: string;
  android_id_sha1: string;
  error_filter_id: string;
  has_conversion: boolean;
  is_pass_through: boolean;
  creative_id: number;
  relationship: TransactionRelationship;
  coupon_code: string;
  redirect_method: string;
  is_sdk_click: boolean;
  category_id: number;
  currency_id: string;
};

type TransactionRelationship = {
  offer: TransactionOffer;
  advertiser: TransactionAdvertiser;
  account_manager: TransactionAccountManager;
  affiliate: TransactionAffiliate;
  affiliate_manager: TransactionAffiliateManager;
  geolocation: TransactionGeolocation;
  device_information: TransactionDeviceInformation;
  http_user_agent: string;
  http_accept_language: string;
  query_parameters: TransactionQueryParameters;
  redirect_url: string;
  forensiq_score: string;
};

type TransactionOffer = {
  network_offer_id: number;
  network_id: number;
  network_advertiser_id: number;
  network_offer_group_id: number;
  name: string;
  offer_status: string;
  network_tracking_domain_id: number;
  visibility: string;
  currency_id: string;
};

type TransactionAdvertiser = {
  network_advertiser_id: number;
  network_id: number;
  name: string;
  account_status: string;
};

type TransactionAccountManager = {
  network_employee_id: number;
  network_id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  account_status: string;
};

type TransactionAffiliate = {
  network_affiliate_id: number;
  network_id: number;
  name: string;
  account_status: string;
  network_traffic_source_id: number;
};

type TransactionAffiliateManager = {
  network_employee_id: number;
  network_id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  account_status: string;
};

type TransactionGeolocation = {
  country_code: string;
  country_name: string;
  region_code: string;
  region_name: string;
  city_name: string;
  dma: number;
  dma_name: string;
  timezone: string;
  carrier_name: string;
  carrier_code: number;
  organization: string;
  isp_name: string;
  is_mobile: boolean;
  is_proxy: boolean;
  postal_code: string;
};

type TransactionDeviceInformation = {
  is_mobile: boolean;
  platform_name: string;
  os_version: string;
  brand: string;
  model: string;
  is_tablet: boolean;
  browser_name: string;
  browser_version: string;
  device_type: string;
  language: string;
  http_accept_language: string;
  is_robot: boolean;
  is_filter: boolean;
};

type TransactionQueryParameters = {
  __cc: string;
  __efckuq: string;
  __qp: string;
  __rf: string;
  _ef_transaction_id: string;
  affid: string;
  async: string;
  oid: string;
  uid: string;
};

type TransactionConversion = {
  conversion_id: string;
  conversion_unix_timestamp: number;
  sub1: string;
  sub2: string;
  sub3: string;
  sub4: string;
  sub5: string;
  source_id: string;
  status: string;
  payout_type: string;
  revenue_type: string;
  payout: number;
  revenue: number;
  session_user_ip: string;
  conversion_user_ip: string;
  country: string;
  region: string;
  city: string;
  dma: number;
  carrier: string;
  platform: string;
  os_version: string;
  device_type: string;
  device_model: string;
  brand: string;
  browser: string;
  language: string;
  http_user_agent: string;
  adv1: string;
  adv2: string;
  adv3: string;
  adv4: string;
  adv5: string;
  is_event: boolean;
  event: string;
  notes: string;
  transaction_id: string;
  click_unix_timestamp: number;
  error_code: number;
  error_message: string;
  sale_amount: number;
  is_scrub: boolean;
  coupon_code: string;
  order_id: string;
  url: string;
  isp: string;
  referer: string;
  app_id: string;
  idfa: string;
  idfa_md5: string;
  idfa_sha1: string;
  google_ad_id: string;
  google_ad_id_md5: string;
  google_ad_id_sha1: string;
  android_id: string;
  android_id_md5: string;
  android_id_sha1: string;
  currency_id: string;
  email: string;
  is_view_through: boolean;
  previous_network_offer_id: number;
  relationship: TransactionRelationship;
  network_offer_payout_revenue_id: number;
};

type TransactionConversions = {
  total: number;
  entries: TransactionConversion[];
};

type TransactionPixels = {
  total: number;
  entries: unknown[];
};

export type EverflowTransactionByIdResponse = {
  click: TransactionClick;
  conversions: TransactionConversions;
  pixels: TransactionPixels;
  raw_click: string;
};

// UPDATE CONVERSION

export type EverflowUpdateConversionPayoutRevenueRequestBody = {
  is_event?: boolean;
  revenue: number;
  payout: number;
};

export type EverflowUpdateConversionPayoutRevenueResponse = {
  result: boolean;
};

// UPDATE CONVERSION STATUS

export type EverflowUpdateConversionStatusRequestBody = {
  conversion_ids: string[];
  conversion_status: "approved" | "rejected";
};

export type EverflowUpdateConversionStatusResponse = {
  result: boolean;
};

export interface EverflowConversionReportResponse {
  conversions: EverflowConversionByIdResponse[];
  paging: { page: number; page_size: number; total_count: number };
}
